import React from "react";
import { Box, Button, Chip, IconButton, Link, Paper, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { PageContainer, SectionHeader, StatCard, DataTable } from "../../Layout/Common";
import { gs, Loader, StaleHint, ensureOptions, canEdit, regionLabel, StateCounts, EventsTable, UsageBar, FIXED, ELLIPSIS,
         n, usdH, usdDay, pct, branchShort, majorMinor, versionLabel, openLogs } from "./servers";

// Game Servers → Overview: the title's fleet at a glance (`servers overview`, polled every 5 s): totals, the rollout
// status per server version, the regions, the counts by state and the latest events (`servers events`). The builds
// (`servers options`) say which build of each branch is the newest one, kept warm.

// One row per (branch, server build) over every region; the rows of one major.minor that run more than one server
// patch are a rollout in progress (§10: the replacement starts first, then the old patch drains)
function rolloutRows(overview, builds) {
    const map = new Map();
    for (const r of overview?.regions ?? []) {
        for (const v of r.versions ?? []) {
            const key = `${v.branch}|${v.version}`;
            const row = map.get(key) ?? { key, branch: v.branch, version: v.version, versionText: v.versionText, profiles: new Set(), regions: new Set(),
                                          servers: 0, ready: 0, slots: 0, slotsUsed: 0, sessions: 0, players: 0 };
            for (const p of v.profiles ?? []) row.profiles.add(p);
            row.regions.add(r.region);
            for (const k of ["servers", "ready", "slots", "slotsUsed", "sessions", "players"]) row[k] += v[k] ?? 0;
            map.set(key, row);
        }
    }
    const rows = [...map.values()];
    const warm = new Map((builds ?? []).filter(b => b.warm).map(b => [b.branch, b]));
    const patches = new Map();   // branch|major.minor -> versions running
    for (const r of rows) {
        const k = `${r.branch}|${majorMinor(r.versionText)}`;
        patches.set(k, [...(patches.get(k) ?? []), r]);
    }
    for (const r of rows) {
        const group = patches.get(`${r.branch}|${majorMinor(r.versionText)}`);
        const newest = group.reduce((a, b) => (b.version > a.version ? b : a));
        r.rolling = group.length > 1 ? (r === newest ? "in" : "out") : null;
        r.otherPatches = group.filter(x => x !== r).map(x => x.versionText);
        const w = warm.get(r.branch);
        r.warm = w ? w.version === r.version : null;
        r.olderLine = w && majorMinor(w.versionText) !== majorMinor(r.versionText);
        // the build row of this major.minor: its newest server patch (options.builds has one entry per branch + major.minor)
        const line = (builds ?? []).find(b => b.branch === r.branch && majorMinor(b.versionText) === majorMinor(r.versionText));
        r.newestPatch = line?.versionText ?? null;
        r.outdated = !!line && r.version < line.version;
        // the newest RUNNING patch is not "rolling in" when a newer patch of the line exists but runs nowhere yet
        if (r.outdated && r.rolling === "in") r.rolling = null;
    }
    rows.sort((a, b) => a.branch.localeCompare(b.branch) || b.version - a.version);
    return rows;
}

export default class ServersOverviewPage extends React.Component {
    state = { overview: null, events: [], options: null, loadedAt: null, stale: null, tick: null, ticking: false };

    // `servers tick`: the gs.tick job's pass for this title now (sweep, then plan); its summary stays on the page
    runTick = () => {
        this.setState({ ticking: true });
        gs("Running a planning pass", ["tick"], { retry: false, timeout: 60000 })
            .then(r => { this.setState({ tick: { at: Date.now(), summary: typeof r?.summary === "string" ? r.summary : JSON.stringify(r?.summary ?? r), changed: r?.changedMachines } }); this.loader.load(); })
            .catch(() => {})
            .finally(() => this.setState({ ticking: false }));
    };

    loader = new Loader(this, async () => {
        ensureOptions(this);
        const [overview, ev] = await Promise.all([
            gs("Loading the overview", ["overview"], { silent: true }),
            gs("Loading events", ["events"], { payload: { limit: 12 }, silent: true }),
        ]);
        return { overview, events: ev?.events ?? [] };
    });

    componentDidMount() {
        this.loader.start();
    }
    componentWillUnmount() { this.loader.stop(); }

    renderTotals() {
        const t = this.state.overview?.totals ?? {};
        const ss = this.state.overview?.sessionStates ?? {};
        const live = Object.values(ss).reduce((a, b) => a + b, 0);
        return (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <StatCard label="Machines" value={`${n(t.ready)} / ${n(t.machines)}`} secondary="ready / total" />
                <StatCard label="Capacity" value={`${n(t.used)} / ${n(t.capacity)}`} secondary={`units used · ${pct(t.used, t.capacity)} %`} />
                <StatCard label="Servers" value={n(t.servers)} secondary={`${n(this.state.overview?.serverStates?.ready)} ready`} />
                <StatCard label="Sessions" value={n(t.sessions)} secondary={`reported · ${n(live)} live in the backend`} />
                <StatCard label="Players" value={n(t.players)} secondary="reported by the servers" />
                <StatCard label="Hourly cost" value={usdH(t.hourlyUsd)} secondary={`≈ ${usdDay(t.hourlyUsd)}`} />
            </Box>
        );
    }

    renderStates() {
        const o = this.state.overview ?? {};
        const box = (label, kind, counts) => (
            <Paper sx={{ p: 1.5, flex: 1, minWidth: 260 }}>
                <Typography variant="overline" color="text.secondary">{label}</Typography>
                <StateCounts kind={kind} counts={counts} />
            </Paper>
        );
        return (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                {box("Machines by state", "machine", o.machineStates)}
                {box("Servers by state", "server", o.serverStates)}
                {box("Live sessions by state", "session", o.sessionStates)}
            </Box>
        );
    }

    renderRollout() {
        const builds = this.state.options?.builds;
        const rows = rolloutRows(this.state.overview, builds);
        const warmBuilds = (builds ?? []).filter(b => b.warm);
        const columns = [
            { id: "branch", label: "Branch", width: 105, render: r => r.branch },
            { id: "version", label: "Server build", width: 210, render: r => (
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
                    <strong>{r.versionText}</strong>
                    {r.warm && <Tooltip title="The newest build of this branch: the one kept warm"><Chip size="small" color="success" label="newest" /></Tooltip>}
                    {r.rolling === "in" && <Tooltip title={`Rolling out: replaces ${r.otherPatches.join(", ")} of the same major.minor. New sessions take its free slots first; scale-up starts this patch`}><Chip size="small" color="info" label="rolling in" /></Tooltip>}
                    {r.rolling === "out" && <Tooltip title={`Being replaced by ${r.otherPatches.join(", ")}. Until it drains (once idle) it still takes new sessions whenever the newer patch has no free slot`}><Chip size="small" color="warning" label="rolling out" /></Tooltip>}
                    {!r.rolling && r.outdated && <Tooltip title={`The newest patch ${r.newestPatch} has no server running here yet (still starting, or its servers failed: a broken build is retried every 5 minutes). New sessions of ${majorMinor(r.versionText)} keep landing on this patch meanwhile`}><Chip size="small" color="warning" variant="outlined" label={`older patch (newest ${r.newestPatch})`} /></Tooltip>}
                    {!r.rolling && !r.outdated && r.olderLine && <Tooltip title="An older major.minor: started on demand, drains after the profile's idle minutes"><Chip size="small" variant="outlined" label="on demand" /></Tooltip>}
                </Box>
            ) },
            { id: "profiles", label: "Profiles", width: 160, sx: ELLIPSIS, render: r => [...r.profiles].join(", ") },
            { id: "regions", label: "Regions", width: 130, sx: ELLIPSIS, render: r => [...r.regions].join(", ") },
            { id: "servers", label: "Servers ready / total", width: 110, align: "right", render: r => `${n(r.ready)} / ${n(r.servers)}` },
            { id: "slots", label: "Slots used / total", width: 120, align: "right", render: r => <>{n(r.slotsUsed)} / {n(r.slots)}<UsageBar used={r.slotsUsed} total={r.slots} /></> },
            { id: "sessions", label: "Sessions", width: 85, align: "right", render: r => n(r.sessions) },
            { id: "players", label: "Players", width: 85, align: "right", render: r => n(r.players) },
        ];
        const rolling = rows.some(r => r.rolling);
        return (
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                    <Typography variant="h6">Rollout status</Typography>
                    {rolling && <Chip size="small" color="info" label="rollout in progress" />}
                    {warmBuilds.length > 0 && <Typography variant="caption" color="text.secondary">
                        newest builds: {warmBuilds.map(b => versionLabel(b.branch, b.versionText)).join(" · ")}
                    </Typography>}
                </Box>
                {rows.length === 0
                    ? <Typography variant="body2" color="text.secondary">No servers.</Typography>
                    : <DataTable columns={columns} rows={rows} getRowKey={r => r.key} sx={FIXED} />}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Each branch keeps its newest major.minor warm (the fleets' warm slots); older major.minors start on demand and drain after
                    the profile's idle minutes. A new server patch rolls out on its own: the replacement starts first when a machine has room
                    (otherwise an idle old server drains first, one at a time), then the old patch drains once idle — meanwhile both patches of
                    the major.minor run side by side. A new session takes a free slot on the newest patch of its major.minor first and falls
                    back to an older patch, so during a rollout, or while the newest patch is broken, sessions keep landing on the older patch
                    instead of waiting; scale-up and on-demand starts always use the newest patch. Any client patch of a major.minor plays on
                    any server patch of it. Persistent worlds never roll or drain by themselves: schedule a stop on their server (Machines)
                    and the world restarts on a new server running the newest patch.
                </Typography>
            </Box>
        );
    }

    renderRegions() {
        const regions = this.state.overview?.regions ?? [];
        const columns = [
            { id: "region", label: "Region", width: 190, sx: ELLIPSIS, render: r => regionLabel(r.region, this.state.options) },
            { id: "machines", label: "Machines ready / total", width: 120, align: "right", render: r => `${n(r.ready)} / ${n(r.machines)}` },
            { id: "capacity", label: "Capacity used / total", width: 130, align: "right", render: r => <>{n(r.used)} / {n(r.capacity)}<UsageBar used={r.used} total={r.capacity} /></> },
            { id: "servers", label: "Servers", width: 80, align: "right", render: r => n(r.servers) },
            { id: "sessions", label: "Sessions", width: 85, align: "right", render: r => n(r.sessions) },
            { id: "players", label: "Players", width: 85, align: "right", render: r => n(r.players) },
            { id: "versions", label: "Versions", width: 300, render: r => (
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {(r.versions ?? []).map(v => <Tooltip key={`${v.branch}-${v.version}`} title={`${v.branch} ${v.versionText}: ${v.ready}/${v.servers} servers ready, ${v.slotsUsed}/${v.slots} slots used`}>
                        <Chip size="small" variant="outlined" label={`${branchShort(v.branch)} ${v.versionText} ×${v.servers}`} />
                    </Tooltip>)}
                </Box>
            ) },
        ];
        return (
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Regions</Typography>
                {regions.length === 0
                    ? <Typography variant="body2" color="text.secondary">No machines yet: deploy one on the Machines page.</Typography>
                    : <DataTable columns={columns} rows={regions} getRowKey={r => r.region} sx={FIXED} />}
            </Box>
        );
    }

    render() {
        const { overview, events, loadedAt, stale } = this.state;
        return (
            <PageContainer maxWidth={1300}>
                <SectionHeader title="Game Servers">
                    <StaleHint loadedAt={loadedAt} stale={stale} />
                    {canEdit() && <Tooltip title="Run the planner for this title now (sweep, then plan; normally every 60 s and on every change)">
                        <span><Button size="small" variant="outlined" startIcon={<PlayArrowIcon />} disabled={this.state.ticking} onClick={this.runTick}>Plan now</Button></span>
                    </Tooltip>}
                    <Tooltip title="Refresh"><IconButton size="small" onClick={this.loader.load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </SectionHeader>
                {this.state.tick && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, whiteSpace: "pre-wrap" }}>
                    Planning pass {new Date(this.state.tick.at).toLocaleTimeString()}: {this.state.tick.summary}
                    {Array.isArray(this.state.tick.changed) ? ` · ${this.state.tick.changed.length} machine(s) reconciled` : this.state.tick.changed != null ? ` · ${this.state.tick.changed} machine(s) reconciled` : ""}
                </Typography>}
                {!overview
                    ? <Typography variant="body2" color="text.secondary">{stale ? `Could not load the overview: ${stale}` : "Loading…"}</Typography>
                    : <>
                        {this.renderTotals()}
                        {this.renderStates()}
                        {this.renderRollout()}
                        {this.renderRegions()}
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, mb: 1 }}>
                            <Typography variant="h6">Latest events</Typography>
                            <Link component="button" underline="hover" onClick={() => openLogs({})}>All events →</Link>
                        </Box>
                        <EventsTable events={events} compact />
                    </>}
            </PageContainer>
        );
    }
}
